import type {
  BulkEditRequest,
  ContactFieldDto,
  ContactSortDto,
  ContactSummaryDto,
} from '@carbonwebmail/contracts'
import { BASE_COLUMNS } from '@carbonwebmail/domain'

/**
 * What the board can show, as data.
 *
 * The six it always has, then one per field the organisation declared. As a
 * list rather than as markup because four things ask about it — what to show,
 * what to sort by, what to draw, and what a column menu may do to it — and four
 * copies of "which columns are there" is four places for one to be missed.
 *
 * **The base six come from the domain** (`BASE_COLUMNS`), not from here. The
 * route that stores a rename refuses an id that is not in that list, so the two
 * have to be the same list or a column could be renamed on screen and refused
 * by the server.
 *
 * **Sortable is not every column.** Segments is a list per row and Receiving is
 * a join, and ordering by either would mean a query that answers a different
 * question from the one the header implies. A column that cannot be sorted says
 * so by not offering it, rather than offering it and quietly doing nothing.
 */

export interface BoardColumn {
  /** Stable, and what a hidden-column list, a view and a rename all name. */
  readonly id: string
  readonly label: string
  /**
   * Which kind of column this is, which is what a menu may do to it.
   *
   * A `base` column is one of the contacts table's own: it can be renamed,
   * because a name is a label, and cannot be deleted or retyped, because the
   * data would still be there. A `field` is a row the organisation created, so
   * all three are theirs.
   */
  readonly kind: 'base' | 'field'
  /** The declared field's key, for a `field`. What a rule and a token name. */
  readonly key: string | undefined
  /** How the rows are ordered by it, when they can be. */
  readonly sort: ContactSortDto | undefined
  /** Always shown: a board with no way back to the contact is not a board. */
  readonly fixed: boolean
  /**
   * How a selection sets this column at once, where it can be set that way.
   *
   * `undefined` is a column the selection bar does not offer, and the domain's
   * `BASE_COLUMNS` says why for each of them. Every declared field can be set,
   * so one arm keyed by its key covers all of them.
   */
  readonly bulkEdit: BulkEditRequest['change']['column'] | undefined
}

/**
 * The board's columns, under the names this organisation uses.
 *
 * `labels` holds only the overrides — a base column with no entry is called
 * whatever the domain says, which is how a default that changes in a later
 * release reaches everybody who never renamed it.
 */
export function boardColumns(
  fields: readonly ContactFieldDto[],
  labels: Readonly<Record<string, string>> = {},
): readonly BoardColumn[] {
  return [
    ...BASE_COLUMNS.map((column) => ({
      id: column.id,
      label: labels[column.id] ?? column.defaultLabel,
      kind: 'base' as const,
      key: undefined,
      sort: column.sort as ContactSortDto | undefined,
      fixed: column.fixed,
      bulkEdit: column.bulkEdit,
    })),
    ...fields.map((field) => ({
      id: `field:${field.key}`,
      label: field.label,
      kind: 'field' as const,
      key: field.key,
      sort: { column: 'field' as const, key: field.key, direction: 'asc' as const },
      fixed: false,
      bulkEdit: 'field' as const,
    })),
  ]
}

/** What a row shows in a declared field's column. Absent and empty read alike. */
export function valueIn(contact: ContactSummaryDto, column: BoardColumn): string {
  return column.key === undefined ? '' : (contact.custom[column.key] ?? '')
}

/**
 * Whether a column is the one being sorted by, and which way.
 *
 * Compared on the column and the key rather than on the whole object, because
 * the direction is what the header is about to toggle.
 */
export function sortedBy(
  column: BoardColumn,
  sort: ContactSortDto | undefined,
): 'asc' | 'desc' | undefined {
  if (sort === undefined || column.sort === undefined) return undefined
  if (sort.column !== column.sort.column) return undefined
  if (sort.column === 'field' && sort.key !== column.sort.key) return undefined
  return sort.direction
}
